import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import TutorialButton from './common/TutorialButton';
import api from '../services/api';
import { tratarErroAPI, logarRequisicao, logarSucesso } from '../utils/errorHandler';
import '../styles/ambienteequipamento.css';
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';
import '../styles/home.css';

const AmbienteEquipamentoManager = () => {
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
  const [formData, setFormData] = useState({
    id_ambiente_equipamento: '',
    id_ambiente: '',
    id_tp_equipamento: '',
    potencia: '',
    altura: '',
    largura: '',
    profundidade: '',
    quantidade: '',
    descr_ambiente_equipamento: '',
    material: '',
    capacidade: ''
  });
  const [apiStatus, setApiStatus] = useState('checking');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [camposComErro, setCamposComErro] = useState([]);
  const [ambientes, setAmbientes] = useState([]);
  const [ambienteSearch, setAmbienteSearch] = useState('');

  const tabulatorRef = useRef(null);
  const tableRef = useRef(null);

  // Filtrar ambientes com base na busca
  const ambientesFiltrados = useMemo(() => {
    if (!ambienteSearch || ambienteSearch.trim() === '') return ambientes;
    const searchTerm = ambienteSearch.toLowerCase().trim();
    return ambientes.filter(amb =>
      amb.id_ambiente?.toLowerCase().includes(searchTerm) ||
      amb.nome_ambiente?.toLowerCase().includes(searchTerm)
    );
  }, [ambientes, ambienteSearch]);

  const mostrarMensagem = useCallback((tipo, texto) => {
    setMensagem({ tipo, texto });
    setTimeout(() => setMensagem({ tipo: '', texto: '' }), 4000);
  }, []);

  const verificarAPI = useCallback(async () => {
    try {
      setApiStatus('checking');
      await api.get('/ambiente-equipamento');
      setApiStatus('connected');
    } catch (error) {
      setApiStatus('error');
      mostrarMensagem('error', `Erro de conexão: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const fetchAmbientes = useCallback(async () => {
    try {
      const response = await api.get('/ambiente');
      setAmbientes(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar ambientes: ${error.message}`);
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    verificarAPI();
    fetchAmbientes();
  }, [verificarAPI, fetchAmbientes]);

  const atualizarRegistro = useCallback(async (id, dadosAtualizacao) => {
    logarRequisicao('Atualizar', 'equipamento do ambiente', { id, dadosAtualizacao });
    try {
      const response = await api.put(`/ambiente-equipamento/${id}`, dadosAtualizacao);
      logarSucesso('Atualizar', 'equipamento do ambiente', response.data);
      if (response && response.data.data) {
        if (tabulatorRef.current) {
          tabulatorRef.current.updateData([response.data.data]);
        }
        mostrarMensagem('success', 'Registro atualizado com sucesso!');
      } else {
        mostrarMensagem('error', 'Resposta da API inválida ao atualizar.');
      }
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'atualizar', 'equipamento do ambiente');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const excluirRegistro = useCallback(async (id) => {
    logarRequisicao('Excluir', 'equipamento do ambiente', { id });
    try {
      const response = await api.delete(`/ambiente-equipamento/${id}`);
      logarSucesso('Excluir', 'equipamento do ambiente', response.data);
      setDados(currentData => currentData.filter(item => item.id_ambiente_equipamento !== id));
      mostrarMensagem('success', 'Registro excluído com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'excluir', 'equipamento do ambiente');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const getColunas = useCallback(() => {
    const colunasBase = [
      {
        title: 'ID Equipamento',
        field: 'id_ambiente_equipamento',
        width: 150,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar ID...',
        sorter: 'string'
      },
      {
        title: 'ID Ambiente',
        field: 'id_ambiente',
        width: 150,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar ID...',
        sorter: 'string',
      },
      {
        title: 'Tipo Equipamento',
        field: 'id_tp_equipamento',
        width: 150,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar tipo de equipamento...',
        sorter: 'string',
      },
      {
        title: 'Potência',
        field: 'potencia',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar potência...',
        sorter: 'number',
        formatter: (cell) => {
          const valor = cell.getValue();
          return valor != null ? Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
        },
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          if (rowValue === null || rowValue === undefined || rowValue === '') return false;

          const formattedValue = Number(rowValue).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          const searchValue = headerValue.trim();

          if (formattedValue.includes(searchValue)) return true;

          const numericValue = rowValue.toString();
          if (numericValue.includes(searchValue.replace(',', '.'))) return true;

          const cleanFormatted = formattedValue.replace(/\./g, '').replace(',', '.');
          const cleanSearch = searchValue.replace(/\./g, '').replace(',', '.');

          return cleanFormatted.includes(cleanSearch);
        },
        editor: (cell, onRendered, success, cancel) => {
          const valor = cell.getValue();
          const valorFormatado = valor != null ? Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
          const input = document.createElement('input');
          input.setAttribute('type', 'text');
          input.style.width = '100%';
          input.value = valorFormatado;

          onRendered(() => {
            input.focus();
            input.select();
          });

          input.addEventListener('blur', () => {
            let valor = input.value.replace(',', '.');
            success(parseFloat(valor) || null);
          });

          input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
              let valor = input.value.replace(',', '.');
              success(parseFloat(valor) || null);
            }
            if (e.key === 'Escape') {
              cancel();
            }
          });

          return input;
        }
      },
      {
        title: 'Altura',
        field: 'altura',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar altura...',
        sorter: 'number',
        formatter: (cell) => {
          const valor = cell.getValue();
          return valor != null ? Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
        },
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          if (rowValue === null || rowValue === undefined || rowValue === '') return false;

          const formattedValue = Number(rowValue).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          const searchValue = headerValue.trim();

          if (formattedValue.includes(searchValue)) return true;

          const numericValue = rowValue.toString();
          if (numericValue.includes(searchValue.replace(',', '.'))) return true;

          const cleanFormatted = formattedValue.replace(/\./g, '').replace(',', '.');
          const cleanSearch = searchValue.replace(/\./g, '').replace(',', '.');

          return cleanFormatted.includes(cleanSearch);
        },
        editor: (cell, onRendered, success, cancel) => {
          const valor = cell.getValue();
          const valorFormatado = valor != null ? Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
          const input = document.createElement('input');
          input.setAttribute('type', 'text');
          input.style.width = '100%';
          input.value = valorFormatado;

          onRendered(() => {
            input.focus();
            input.select();
          });

          input.addEventListener('blur', () => {
            let valor = input.value.replace(',', '.');
            success(parseFloat(valor) || null);
          });

          input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
              let valor = input.value.replace(',', '.');
              success(parseFloat(valor) || null);
            }
            if (e.key === 'Escape') {
              cancel();
            }
          });

          return input;
        }
      },
      {
        title: 'Largura',
        field: 'largura',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar largura...',
        sorter: 'number',
        formatter: (cell) => {
          const valor = cell.getValue();
          return valor != null ? Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
        },
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          if (rowValue === null || rowValue === undefined || rowValue === '') return false;

          const formattedValue = Number(rowValue).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          const searchValue = headerValue.trim();

          if (formattedValue.includes(searchValue)) return true;

          const numericValue = rowValue.toString();
          if (numericValue.includes(searchValue.replace(',', '.'))) return true;

          const cleanFormatted = formattedValue.replace(/\./g, '').replace(',', '.');
          const cleanSearch = searchValue.replace(/\./g, '').replace(',', '.');

          return cleanFormatted.includes(cleanSearch);
        },
        editor: (cell, onRendered, success, cancel) => {
          const valor = cell.getValue();
          const valorFormatado = valor != null ? Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
          const input = document.createElement('input');
          input.setAttribute('type', 'text');
          input.style.width = '100%';
          input.value = valorFormatado;

          onRendered(() => {
            input.focus();
            input.select();
          });

          input.addEventListener('blur', () => {
            let valor = input.value.replace(',', '.');
            success(parseFloat(valor) || null);
          });

          input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
              let valor = input.value.replace(',', '.');
              success(parseFloat(valor) || null);
            }
            if (e.key === 'Escape') {
              cancel();
            }
          });

          return input;
        }
      },
      {
        title: 'Profundidade',
        field: 'profundidade',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar profundidade...',
        sorter: 'number',
        formatter: (cell) => {
          const valor = cell.getValue();
          return valor != null ? Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
        },
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          if (rowValue === null || rowValue === undefined || rowValue === '') return false;

          const formattedValue = Number(rowValue).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          const searchValue = headerValue.trim();

          if (formattedValue.includes(searchValue)) return true;

          const numericValue = rowValue.toString();
          if (numericValue.includes(searchValue.replace(',', '.'))) return true;

          const cleanFormatted = formattedValue.replace(/\./g, '').replace(',', '.');
          const cleanSearch = searchValue.replace(/\./g, '').replace(',', '.');

          return cleanFormatted.includes(cleanSearch);
        },
        editor: (cell, onRendered, success, cancel) => {
          const valor = cell.getValue();
          const valorFormatado = valor != null ? Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
          const input = document.createElement('input');
          input.setAttribute('type', 'text');
          input.style.width = '100%';
          input.value = valorFormatado;

          onRendered(() => {
            input.focus();
            input.select();
          });

          input.addEventListener('blur', () => {
            let valor = input.value.replace(',', '.');
            success(parseFloat(valor) || null);
          });

          input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
              let valor = input.value.replace(',', '.');
              success(parseFloat(valor) || null);
            }
            if (e.key === 'Escape') {
              cancel();
            }
          });

          return input;
        }
      },
      {
        title: 'Quantidade',
        field: 'quantidade',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar quantidade...',
        sorter: 'number',
      },
      {
        title: 'Descrição',
        field: 'descr_ambiente_equipamento',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar descrição...',
        sorter: 'string',
      },
      {
        title: 'Material',
        field: 'material',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar material...',
        sorter: 'string',
      },
      {
        title: 'Capacidade',
        field: 'capacidade',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar capacidade...',
        sorter: 'string',
      }
    ];

    return [
      {
        title: 'Salvar',
        field: 'salvar',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-save"><i class="fas fa-save"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          atualizarRegistro(data.id_ambiente_equipamento, data);
        },
        headerSort: false
      },
      {
        title: 'Excluir',
        field: 'excluir',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-delete"><i class="fas fa-trash"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          if (window.confirm(`Deseja realmente excluir o registro "${data.id_ambiente_equipamento}"?`)) {
            excluirRegistro(data.id_ambiente_equipamento);
          }
        },
        headerSort: false
      },
      ...colunasBase.map(col => {
        if (col.field === 'id_ambiente_equipamento') {
          // ID Equipamento não é editável pois é a chave primária
          return col;
        }
        // Campos numéricos já têm seus editores configurados
        if (['potencia', 'altura', 'largura', 'profundidade'].includes(col.field)) {
          return col;
        }
        // Demais campos são editáveis como texto
        return { ...col, editor: 'input' };
      })
    ];
  }, [atualizarRegistro, excluirRegistro]);

  const carregarDados = useCallback(async (showMessage = true) => {
    try {
      setLoading(true);
      const response = await api.get('/ambiente-equipamento');
      setDados(response.data.data || []);
      setLastUpdate(new Date());
      if (showMessage) {
        mostrarMensagem('success', `${response.data.data?.length || 0} registros carregados`);
      }
    } catch (error) {
      mostrarMensagem('error', error.message);
      setDados([]);
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    if (apiStatus === 'connected') {
      carregarDados(false);
    }
  }, [apiStatus, carregarDados]);

  const inicializarTabulator = useCallback(() => {
    if (!tableRef.current) return;
    if (tabulatorRef.current) tabulatorRef.current.destroy();

    tabulatorRef.current = new Tabulator(tableRef.current, {
      data: dados,
      columns: getColunas(),
      layout: 'fitColumns',
      pagination: 'local',
      paginationSize: 10,
      paginationCounter: 'rows',
      movableColumns: true,
      resizableRows: true,
      tooltips: true,
      placeholder: 'Nenhum registro encontrado',
      index: 'id_ambiente_equipamento'
    });

    const style = document.createElement('style');
    style.textContent = `
      .btn-save {
        background: #007bff;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-save:hover {
        background: #0056b3;
      }
      .btn-delete {
        background: #dc3545;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-delete:hover {
        background: #c82333;
      }
      .btn-back {
        background: transparent;
        border: none;
        font-size: 24px;
        cursor: pointer;
      }
    `;
    document.head.appendChild(style);
  }, [dados, getColunas]);

  useEffect(() => {
    if (apiStatus === 'connected' && tableRef.current) {
      if (!tabulatorRef.current) {
        inicializarTabulator();
      } else {
        tabulatorRef.current.setColumns(getColunas());
        tabulatorRef.current.setData(dados);
      }
    }
  }, [dados, apiStatus, getColunas, inicializarTabulator]);

  const adicionarRegistro = async (e) => {
    e.preventDefault();
    const { id_ambiente_equipamento, id_ambiente, id_tp_equipamento, potencia, altura, largura, profundidade, quantidade, descr_ambiente_equipamento, material, capacidade } = formData;

    // Validação de campos obrigatórios com mensagens específicas
    const camposObrigatorios = [];
    const erros = [];

    if (!id_ambiente_equipamento) {
      camposObrigatorios.push('ID Equipamento');
      erros.push('id_ambiente_equipamento');
    }
    if (!id_ambiente) {
      camposObrigatorios.push('ID Ambiente');
      erros.push('id_ambiente');
    }
    if (!id_tp_equipamento) {
      camposObrigatorios.push('Tipo Equipamento');
      erros.push('id_tp_equipamento');
    }

    if (camposObrigatorios.length > 0) {
      setCamposComErro(erros);
      mostrarMensagem('error', `Por favor, preencha os campos obrigatórios: ${camposObrigatorios.join(', ')}`);
      return;
    }

    setCamposComErro([]);

    const dataToSend = {
      id_ambiente_equipamento,
      id_ambiente,
      id_tp_equipamento,
      potencia: potencia === '' ? null : potencia,
      altura: altura === '' ? null : altura,
      largura: largura === '' ? null : largura,
      profundidade: profundidade === '' ? null : profundidade,
      quantidade: quantidade === '' ? null : quantidade,
      descr_ambiente_equipamento: descr_ambiente_equipamento === '' ? null : descr_ambiente_equipamento,
      material: material === '' ? null : material,
      capacidade: capacidade === '' ? null : capacidade,
    };

    logarRequisicao('Adicionar', 'equipamento do ambiente', dataToSend);
    try {
      setLoading(true);
      const response = await api.post('/ambiente-equipamento', dataToSend);
      logarSucesso('Adicionar', 'equipamento do ambiente', response.data);
      setDados([...dados, response.data.data]);
      setFormData({ id_ambiente_equipamento: '', id_ambiente: '', id_tp_equipamento: '', potencia: '', altura: '', largura: '', profundidade: '', quantidade: '', descr_ambiente_equipamento: '', material: '', capacidade: '' });
      setAmbienteSearch('');
      setCamposComErro([]);
      mostrarMensagem('success', 'Registro adicionado com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'adicionar', 'equipamento do ambiente');
      mostrarMensagem('error', mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));

    // Remove o erro do campo quando ele for preenchido
    if (value || type === 'checkbox') {
      setCamposComErro(prev => prev.filter(campo => campo !== name));
    }
  };

  const exportarDados = () => {
    if (tabulatorRef.current) {
      tabulatorRef.current.download('csv', 'ambiente_equipamentos.csv');
    }
  };

  return (
    <div className="tipo-ambiente-container">
      <div className="header-controls">
        <button className="btn-back" onClick={() => window.history.back()}><i class="fas fa-arrow-left"></i></button>
      </div>

      <div className="header">
        <div className="title-container">
          <img src={etgLogo} alt="ETG Logo" className="logo" />
          <h1>Gerenciador de Equipamentos do Ambiente</h1>
          <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
        </div>
        <p>Interface para popular e gerenciar a tabela tb_ambiente_equipamento</p>
        <div style={{ marginTop: '10px' }}>
          <TutorialButton videoPath="bdados/ambiente equipamento.mov" buttonText="Ver Tutorial Ambiente Equipamento" />
        </div>
      </div>

      {mensagem.texto && (
        <div className="status-container">
          <div className={`status ${mensagem.tipo}`}>{mensagem.texto}</div>
        </div>
      )}

      <div className="controls">
        <form onSubmit={adicionarRegistro} className="form-inline">
          <div className="form-group">
            <label htmlFor="id_ambiente_equipamento">ID Equipamento:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="text"
              id="id_ambiente_equipamento"
              name="id_ambiente_equipamento"
              value={formData.id_ambiente_equipamento}
              onChange={handleInputChange}
              placeholder="Ex: EQUIP-001"
              maxLength={255}
              disabled={loading || apiStatus !== 'connected'}
              className={camposComErro.includes('id_ambiente_equipamento') ? 'input-error' : ''}
            />
          </div>
          <div className="form-group">
            <label htmlFor="id_ambiente">ID Ambiente:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="text"
              id="id_ambiente"
              name="id_ambiente"
              value={ambienteSearch}
              onChange={(e) => {
                setAmbienteSearch(e.target.value);
                const ambienteEncontrado = ambientes.find(amb =>
                  amb.id_ambiente === e.target.value ||
                  `${amb.id_ambiente} - ${amb.nome_ambiente || ''}`.trim() === e.target.value
                );
                if (ambienteEncontrado) {
                  setFormData(prev => ({ ...prev, id_ambiente: ambienteEncontrado.id_ambiente }));
                  setCamposComErro(prev => prev.filter(c => c !== 'id_ambiente'));
                } else {
                  setFormData(prev => ({ ...prev, id_ambiente: '' }));
                }
              }}
              list="ambientes-list-equipamento"
              placeholder="Digite para buscar ambiente..."
              disabled={loading || apiStatus !== 'connected'}
              className={camposComErro.includes('id_ambiente') ? 'input-error' : ''}
              autoComplete="off"
            />
            <datalist id="ambientes-list-equipamento">
              {ambientesFiltrados.map(item => (
                <option key={item.id_ambiente} value={item.id_ambiente}>
                  {item.nome_ambiente || item.id_ambiente}
                </option>
              ))}
            </datalist>
          </div>
          <div className="form-group">
            <label htmlFor="id_tp_equipamento">Tipo Equipamento:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="text"
              id="id_tp_equipamento"
              name="id_tp_equipamento"
              value={formData.id_tp_equipamento}
              onChange={handleInputChange}
              placeholder="Ex: TPE-001"
              maxLength={255}
              disabled={loading || apiStatus !== 'connected'}
              className={camposComErro.includes('id_tp_equipamento') ? 'input-error' : ''}
            />
          </div>
          <div className="form-group">
            <label htmlFor="potencia">Potência:</label>
            <input type="number" id="potencia" name="potencia" value={formData.potencia} onChange={handleInputChange} placeholder="Ex: 1500" disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="altura">Altura:</label>
            <input type="number" step="0.01" id="altura" name="altura" value={formData.altura} onChange={handleInputChange} placeholder="Ex: 1,80" disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="largura">Largura:</label>
            <input type="number" step="0.01" id="largura" name="largura" value={formData.largura} onChange={handleInputChange} placeholder="Ex: 0,60" disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="profundidade">Profundidade:</label>
            <input type="number" step="0.01" id="profundidade" name="profundidade" value={formData.profundidade} onChange={handleInputChange} placeholder="Ex: 0,50" disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="quantidade">Quantidade:</label>
            <input type="number" id="quantidade" name="quantidade" value={formData.quantidade} onChange={handleInputChange} placeholder="Ex: 10" disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="descr_ambiente_equipamento">Descrição:</label>
            <input type="text" id="descr_ambiente_equipamento" name="descr_ambiente_equipamento" value={formData.descr_ambiente_equipamento} onChange={handleInputChange} placeholder="Ex: Ar condicionado" maxLength={255} disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="material">Material:</label>
            <input type="text" id="material" name="material" value={formData.material} onChange={handleInputChange} placeholder="Ex: Plástico" maxLength={255} disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="capacidade">Capacidade:</label>
            <input type="text" id="capacidade" name="capacidade" value={formData.capacidade} onChange={handleInputChange} placeholder="Ex: 12000 BTUs" maxLength={255} disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-actions-row">
            <div className="form-buttons">
              <button type="submit" className="btn btn-success" disabled={loading || apiStatus !== 'connected'}>
                {loading ? '⏳ Adicionando...' : '➕ Adicionar'}
              </button>
              <button className="btn btn-primary" onClick={() => carregarDados(true)} disabled={loading || apiStatus !== 'connected'}>
                {loading ? '⏳ Carregando...' : '🔄 Recarregar'}
              </button>
              <button className="btn btn-secondary" onClick={exportarDados} disabled={dados.length === 0}>📥 Exportar CSV</button>
            </div>
            <div className="info-campos-obrigatorios">
              <i className="fas fa-info-circle"></i>
              <span>* indica que os campos são obrigatorios.</span>
            </div>
          </div>
        </form>
      </div>

      <div className="stats-bar">
        <div className="stats-item"><strong>{dados.length}</strong> registros carregados</div>
        <div className="stats-item">Última atualização: <strong>{lastUpdate ? lastUpdate.toLocaleTimeString() : 'N/A'}</strong></div>
      </div>

      <div className="tabulator-container">
        <div ref={tableRef}></div>
      </div>

    </div>
  );
};

export default AmbienteEquipamentoManager;